import { withClientVersion } from './clientVersion';
import { trackEvent } from './analytics';
import { readPurpose, readVisibility, readEventSource } from './readContext';
import { retryAfterMilliseconds } from './retryRead.mjs';

const READ_OPERATIONS = [
  [/^\/(?:v[12]\/)?history(?:\/query)?$/, 'history'], [/^\/(?:v[12]\/)?quota$/, 'quota'],
  [/^\/(?:v[12]\/)?status\/[^/]+$/, 'status_poll'],
  [/^\/(?:v[12]\/)?result\/[^/]+$/, 'result'],
  [/^\/imports(?:\/[^/]+)?$/, 'import_status'],
];
const OPERATIONS = [...READ_OPERATIONS, [/^\/(?:functions\/v1\/)?create-checkout-session$/, 'checkout'],
  [/^\/podcast\/resolve$/, 'podcast_resolve'],[/^\/podcast\/ingest$/, 'podcast_import'],
  [/^\/(?:v[12]\/)?start$/, 'transcription_start'],
  [/^\/result\/[^/]+\/speakers$/, 'speaker_names_save']];
const responseDiagnostics = new WeakMap();
const responseCleanup = new WeakMap();
const emptyJsonReaders = new WeakMap();
const slowReadTimes = new Map();
function serverMeasurements(response) {
  const header = response.headers?.get?.('Server-Timing') || '';
  const values = {};
  for (const item of header.split(',')) {
    const match = item.trim().match(/^(auth|db|render);\s*dur=([\d.]+)$/);
    if (!match) continue;
    const value = Number(match[2]);
    if (Number.isFinite(value) && value >= 0 && value <= 86400000) values[match[1] + '_ms'] = value;
  }
  return values;
}
export function requestOperation(url) {
  try {
    const path = new URL(url, 'https://api.instanttranscriber.com').pathname;
    return OPERATIONS.find(([pattern]) => pattern.test(path))?.[1];
  } catch { return undefined; }
}

export async function fetchWithTimeout(url, options = {}, timeoutMs = 15000) {
  const { requestPurpose, ...fetchOptions } = options;
  const controller = new AbortController();
  const started = Date.now();
  const operation = requestOperation(url);
  const purpose = readPurpose(operation,requestPurpose);
  const startedVisibility = readVisibility();
  const slowReadKey = `${operation}:${purpose || 'unknown'}`;
  const isRead = ['history', 'quota', 'status_poll', 'result', 'import_status'].includes(operation);
  let terminationReason;
  let finished = false;
  let timedOut = false;
  let headersAt;
  let serverTimes = {};
  const measurements = () => ({ ...serverTimes,
    ...(isRead ? {event_source:readEventSource(operation,purpose,startedVisibility)} : {}),
    ...(headersAt !== undefined ? { header_ms: Math.max(0, headersAt - started), body_ms: Math.max(0, Date.now() - headersAt) } : {}) });
  const decorateFailure = error => {
    const failure = timedOut ? Object.assign(new Error('The request timed out.'), { code: 'request_timeout' })
      : controller.signal.aborted ? Object.assign(new Error('The request was cancelled.'), { name: 'AbortError', code: 'request_cancelled' })
      : error instanceof SyntaxError ? Object.assign(new Error('The server returned an invalid response.'), { code: 'malformed_response' })
      : error && typeof error === 'object' ? error : new Error('The request failed.');
    return Object.assign(failure, { operation, elapsed_ms: Math.max(0, Date.now() - started),
      ...measurements(),
      termination_reason: terminationReason || (error instanceof SyntaxError ? 'parse' : 'network') });
  };
  const abort = () => {
    if (finished) return;
    const reason = options.signal?.reason;
    terminationReason ||= ['navigation', 'view_closed', 'account_changed', 'replaced', 'cancelled'].includes(reason) ? reason : 'cancelled';
    controller.abort();
  };
  const navigate = () => { if (!finished) { terminationReason = 'navigation'; controller.abort(); } };
  options.signal?.addEventListener('abort', abort, { once: true });
  if (options.signal?.aborted) abort();
  if (operation && typeof window !== 'undefined') window.addEventListener('pagehide', navigate);
  const cleanup = () => {
    finished = true;
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', abort);
    if (operation && typeof window !== 'undefined') window.removeEventListener('pagehide', navigate);
  };
  const timer = setTimeout(() => { timedOut = true; terminationReason = 'timeout'; controller.abort(); cleanup(); }, timeoutMs);
  // Race the whole body read as well: custom fetch adapters can ignore abort.
  const untilAborted = async read => {
    if (controller.signal.aborted) throw decorateFailure(new Error('Aborted'));
    let stop;
    const aborted = new Promise((_, reject) => {
      stop = () => reject(decorateFailure(new Error('Aborted')));
      controller.signal.addEventListener('abort', stop, { once: true });
    });
    try { return await Promise.race([Promise.resolve().then(read), aborted]); }
    finally { controller.signal.removeEventListener('abort', stop); }
  };
  try {
    let target = isRead ? withClientVersion(url) : url;
    if (purpose) target += `${target.includes('?') ? '&' : '?'}request_purpose=${purpose}`;
    if (isRead) target += `${target.includes('?') ? '&' : '?'}workspace_visibility=${startedVisibility}`;
    const response = await untilAborted(() => fetch(target, { ...fetchOptions, signal: controller.signal }));
    headersAt = Date.now();
    serverTimes = serverMeasurements(response);
    responseDiagnostics.set(response, { operation, elapsed_ms: Math.max(0, headersAt - started), ...measurements(), termination_reason: 'response' });
    responseCleanup.set(response, cleanup);
    const readBody = async read => {
      try {
        const value = await untilAborted(read);
        // Parsing can block the event loop past the deadline before its timer
        // gets a turn. Check the elapsed time explicitly before accepting it.
        if (!finished && Date.now() - started >= timeoutMs) {
          timedOut = true; terminationReason = 'timeout'; controller.abort();
          throw decorateFailure(new Error('Aborted'));
        }
        const elapsed_ms = Math.max(0, Date.now() - started);
        const timing = { operation, elapsed_ms, ...measurements(), termination_reason: 'response' };
        responseDiagnostics.set(response, timing);
        if (isRead && response.ok && elapsed_ms >= 2000 && Date.now() - (slowReadTimes.get(slowReadKey) || 0) >= 60000) {
          if (trackEvent('api_read_slow', timing)) slowReadTimes.set(slowReadKey, Date.now());
        }
        return value;
      } catch (error) {
        if (!finished && !controller.signal.aborted && Date.now() - started >= timeoutMs) {
          timedOut = true; terminationReason = 'timeout'; controller.abort();
        }
        const failure = decorateFailure(error);
        responseDiagnostics.set(response, { operation, elapsed_ms: failure.elapsed_ms, ...measurements(), termination_reason: failure.termination_reason });
        if (response.status >= 400) failure.status = response.status;
        throw failure;
      } finally { cleanup(); }
    };
    if (typeof response.text === 'function') {
      const text = response.text.bind(response);
      emptyJsonReaders.set(response, () => readBody(async () => {
        const body = await text();
        return body ? JSON.parse(body) : null;
      }));
    }
    // Keep native Response properties and methods; hold the same deadline until
    // its body has actually arrived and JSON parsing has finished.
    let readable = false;
    for (const method of ['json', 'text', 'blob', 'arrayBuffer', 'formData']) {
      if (typeof response[method] !== 'function') continue;
      readable = true;
      const read = response[method].bind(response);
      response[method] = (...args) => readBody(() => read(...args));
    }
    if (!readable || response.body === null) cleanup();
    return response;
  } catch (error) {
    cleanup();
    throw decorateFailure(error);
  }
}

export function discardResponse(response) {
  responseCleanup.get(response)?.();
  // Cancel an unread stream without keeping a timer or navigation listener.
  try { response.body?.cancel()?.catch(() => {}); } catch {}
}

export async function readJsonResponse(response, { allowEmpty = false } = {}) {
  try {
    if (!allowEmpty) return await response.json();
    if (emptyJsonReaders.has(response)) return await emptyJsonReaders.get(response)();
    const text = await response.text();
    try { return text ? JSON.parse(text) : null; }
    catch { throw Object.assign(new Error('The server returned an invalid response.'), {
      ...responseDiagnostics.get(response), code: 'malformed_response', termination_reason: 'parse' }); }
  }
  catch (error) {
    // A non-JSON HTTP error still has its useful status. Transport failures and
    // successful-but-malformed replies retain their own diagnostics.
    if (!response.ok && (error?.code === 'malformed_response' || error instanceof SyntaxError)) return {};
    throw error;
  }
}

export function responseError(response, payload = {}, fallback = 'Request failed') {
  return Object.assign(new Error(payload?.error || payload?.message || fallback), {
    ...responseDiagnostics.get(response), termination_reason: 'http_error',
    status: response.status, code: payload?.code,
    retryAfterMs: retryAfterMilliseconds(response.headers),
  });
}
